import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux/es/exports";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import swal from "sweetalert";
import {
  getCountryProviders,
  createCountryProvider,
  updateCountryProvider,
  deleteCountryProvider,
} from "../../services/countryProviderService";

const PROVIDER_OPTIONS = ["SMS", "TELEGRAM", "WHATSAPP", "EMAIL"];

const emptyForm = { provider: "SMS", sortOrder: 0, isActive: true };

const CountryProviders = ({ countryId }) => {
  const { t } = useTranslation();
  const { token } = useSelector((state) => state.user);

  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = not editing, 0 = adding new
  const [form, setForm] = useState(emptyForm);

  const fetchProviders = async () => {
    setIsLoading(true);
    try {
      const data = await getCountryProviders(token, countryId);
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch country providers error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (countryId) fetchProviders();
  }, [countryId]);

  const startAdd = () => {
    setForm(emptyForm);
    setEditingId(0);
  };

  const startEdit = (item) => {
    setForm({
      id: item.id,
      provider: item.provider,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
    setEditingId(item.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    try {
      const body = {
        ...form,
        countryId: parseInt(countryId),
        sortOrder: parseInt(form.sortOrder) || 0,
      };

      let response;
      if (editingId === 0) {
        response = await createCountryProvider(token, body);
      } else {
        response = await updateCountryProvider(token, { ...body, id: editingId });
      }

      if (response?.id || response?.success || !response?.error) {
        toast.success(
          editingId === 0
            ? t("countryPage.providers.add_success")
            : t("countryPage.providers.edit_success"),
          { position: "bottom-right", autoClose: 3000 }
        );
        cancelEdit();
        fetchProviders();
      } else {
        toast.error(t("countryPage.providers.error"), {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("Save country provider error:", err);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await swal({
      title: t("action.delete_item.title"),
      text: t("action.delete_item.text"),
      icon: "warning",
      buttons: { cancel: true, confirm: true },
    });
    if (!confirmed) return;

    try {
      const response = await deleteCountryProvider(token, id);
      if (response?.success) {
        toast.success(t("action.delete_success"), {
          position: "bottom-right",
          autoClose: 3000,
        });
        fetchProviders();
      }
    } catch (err) {
      console.error("Delete country provider error:", err);
    }
  };

  return (
    <div className="col-span-6 mt-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">
          {t("countryPage.providers.title")}
        </h3>
        {editingId === null && (
          <button
            type="button"
            onClick={startAdd}
            className="text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5 font-medium"
          >
            + {t("countryPage.providers.add")}
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">...</p>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  {t("countryPage.providers.provider")}
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  {t("countryPage.providers.sort_order")}
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">
                  {t("countryPage.providers.is_active")}
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {providers.map((item) =>
                editingId === item.id ? (
                  <ProviderFormRow
                    key={item.id}
                    form={form}
                    setForm={setForm}
                    onSave={handleSave}
                    onCancel={cancelEdit}
                    t={t}
                  />
                ) : (
                  <tr key={item.id}>
                    <td className="px-4 py-2 font-medium text-gray-800">
                      {item.provider}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{item.sortOrder}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {item.isActive
                          ? t("countryPage.providers.active")
                          : t("countryPage.providers.inactive")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                      >
                        {t("countryPage.providers.edit")}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                      >
                        {t("countryPage.providers.delete")}
                      </button>
                    </td>
                  </tr>
                )
              )}

              {editingId === 0 && (
                <ProviderFormRow
                  form={form}
                  setForm={setForm}
                  onSave={handleSave}
                  onCancel={cancelEdit}
                  t={t}
                />
              )}

              {providers.length === 0 && editingId !== 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-4 text-center text-gray-400 text-sm"
                  >
                    {t("countryPage.providers.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProviderFormRow = ({ form, setForm, onSave, onCancel, t }) => (
  <tr className="bg-indigo-50">
    <td className="px-4 py-2">
      <select
        value={form.provider}
        onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))}
        className="w-full border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
      >
        {PROVIDER_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </td>
    <td className="px-4 py-2">
      <input
        type="number"
        value={form.sortOrder}
        onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
        className="w-full border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
      />
    </td>
    <td className="px-4 py-2">
      <input
        type="checkbox"
        checked={form.isActive}
        onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
        className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
      />
    </td>
    <td className="px-4 py-2 text-right space-x-2">
      <button
        type="button"
        onClick={onSave}
        className="text-white bg-indigo-600 hover:bg-indigo-700 text-xs font-medium px-2.5 py-1 rounded-md"
      >
        {t("save")}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-gray-600 hover:text-gray-800 text-xs font-medium px-2.5 py-1 rounded-md border border-gray-300"
      >
        {t("cancel")}
      </button>
    </td>
  </tr>
);

export default CountryProviders;
